import { useState, useEffect, useCallback } from 'react';
import {
  createCommandeAchat,
  saveCommandeAchat,
  getCommandeAchat,
  listCommandesAchat,
  getItemsInCommandeAchat,
  addItemToCommandeAchat,
  deleteItemFromCommandeAchat,
  validateCommandeAchat,
  transferCommandeToReception,
  listDBFFiles,
  importFromDBF,
} from '../models/achatCommandeAPI';

export const useFetchAllCommandesAchat = (params) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listCommandesAchat(params);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return { items, total, loading, error, refetch: fetchData };
};

export const useFetchCommandeAchatById = (id) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchOne = useCallback(async () => {
    if (!id) return;
    try { setLoading(true); const res = await getCommandeAchat(id); setData(res.data); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchOne(); }, [fetchOne]);
  return { data, loading, error, refetch: fetchOne };
};

export const useFetchItemsInCommandeAchat = (id) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchItems = useCallback(async () => {
    if (!id) return;
    try { setLoading(true); const res = await getItemsInCommandeAchat(id); setItems(res.data || []); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  return { items, loading, error, refetch: fetchItems };
};

export const useCreateCommandeAchat = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const create = async (payload) => {
    try { setLoading(true); const res = await createCommandeAchat(payload); return res.data; }
    catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  };
  return { create, loading, error };
};

export const useSaveCommandeAchat = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const save = async (commande, items) => {
    try { setLoading(true); const res = await saveCommandeAchat({ commande, items }); return res.data; }
    catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  };
  return { save, loading, error };
};

export const useAddItemToCommandeAchat = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const add = async (ref, item) => {
    try { setLoading(true); await addItemToCommandeAchat(ref, item); }
    catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  };
  return { add, loading, error };
};

export const useDeleteItemFromCommandeAchat = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const remove = async (ref, lineId) => {
    try { setLoading(true); await deleteItemFromCommandeAchat(ref, lineId); }
    catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  };
  return { remove, loading, error };
};

export const useValidateCommandeAchat = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isValidated, setIsValidated] = useState(false);

  const validate = async (id) => {
    try {
      setLoading(true);
      setError('');
      await validateCommandeAchat(id);
      setIsValidated(true);
    } catch (e) {
      setError(e.message);
      setIsValidated(false);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { validate, loading, error, isValidated };
};

export const useTransferCommandeToReception = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isTransferred, setIsTransferred] = useState(false);

  const transfer = async (ref) => {
    try {
      setLoading(true);
      setError('');
      const res = await transferCommandeToReception(ref);
      setIsTransferred(true);
      return res.data;
    } catch (e) {
      setError(e.message);
      setIsTransferred(false);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { transfer, loading, error, isTransferred };
};

export const useListDBFFiles = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await listDBFFiles();
      setFiles(res.data || []);
      return res.data;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { files, fetchFiles, loading, error };
};

export const useImportFromDBF = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const importData = async (fileName) => {
    try {
      setLoading(true);
      setError('');
      const res = await importFromDBF(fileName);
      return res.data;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { importData, loading, error };
};

